import { Fragment, Suspense, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  line, Color, LIST_PAGES, NEW_DOCUMENT_ID, Size, assetRoute, billActions, billTakesCredit, billSubmissionOutcome, documentPageMode, billPills, pageKindLineText, t, type BillAction, type BillAnswer, type BillAssetPrefill, type BillSubmissionAnswer, type PrintDocument, formatPercent
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { MakeRecurringButton } from '../accounting/MakeRecurringButton';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Money } from '../../components/Money';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { ReasonWindow } from '../../components/modals/ReasonWindow';
import { AccessDenied } from '../../shell/Fallbacks';
import { ApplyCreditModal } from './ApplyCreditModal';
import { ApprovalReason } from './ApprovalReason';
import { BillForm } from './BillForm';
import { BannerPills } from './BannerPills';
import { DocumentViewPage, type DocumentLinesDescriptor } from './DocumentViewPage';
import { BILLS_PATH, billLineAssetPath, get, post, useApi } from './api';
import { emptyNodeDraft, nodeBody, type NodeDraft } from '../assets/nodeDraft';
import { lazyGroup } from '../../shell/lazyGroup';
import { NodeFormModal } from './lazyNodeFormModal';
import { refreshMenuCounts } from '../../shell/menuCounts';
import { documentPdfPath } from './ViewPdfLink';
import { type ScreenFact } from './PageFacts';
import styles from './DocumentPage.module.css';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { AccountJournalTile } from '../accounting/AccountJournalTile';
import { DocumentFilesPanel } from './DocumentFilesPanel';
import { vendorReleaseCompanies } from './releaseFiles';
import { DocumentHistoryPanel } from './DocumentHistoryPanel';
import { type TileRow } from './tileItems';
import { useViewerZone } from '../../shell/ViewerZone';

const ACTION_LABEL: Record<BillAction, () => string> = {
  submit: () => t('bill.action.submit'),
  approve: () => t('bill.action.approve'),
  reject: () => t('bill.action.reject'),
  resubmit: () => t('bill.action.resubmit'),
  pay: () => t('bill.action.pay'),
  void: () => t('bill.action.void'),
};

type OpenModal = 'reject' | 'void' | 'pay' | null;

// 🔨 The register's node form and the Pay Bill window load when a bill line first opens it, never with the bill page (piece 12; decision 10641).
const PayBillModal = lazyGroup(() => import('../banking/PayBillModal'))('PayBillModal');

/**
 * The Bill's page (piece 24; decisions 0408, 0409): the banner with its
 * number, the lifecycle and approval axes as pills, and the facts; the print
 * projection as the body of view mode; the lines with their variance beneath
 * each; the vendor's available credit; the delivery confirmation where the
 * goods have not landed; and the footer's one set of actions per status.
 *
 * 🔨 Every action is a route the server refuses on its own terms. The page
 * offers only what the status allows, so a refusal is an exception rather than
 * the ordinary answer.
 */
export function BillPage({ billId, apiBaseUrl = '' }: { billId: string; apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const zone = useViewerZone();
  const [params] = useSearchParams();
  const creating = billId === NEW_DOCUMENT_ID;
  const editing = documentPageMode(params.get('mode'), billId) === 'edit';
  const page = useApi<BillAnswer & { history?: TileRow[]; canMakeRecurring?: boolean }>(editing ? null : `${BILLS_PATH}/${billId}`, apiBaseUrl);
  const print = useApi<PrintDocument>(editing ? null : `${BILLS_PATH}/${billId}/print`, apiBaseUrl);
  const [modal, setModal] = useState<OpenModal>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Piece 27, Vendor credit application: the modal a person applies a named credit through; nothing else applies one. On a bill not ready it is View credits (T740).
  const [applyCreditOpen, setApplyCreditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // 🔒 Decision 9700: the New Asset window a fixed-asset line's Create Asset opens, filled from the line, and the save's refusal inside it.
  const [assetFrom, setAssetFrom] = useState<BillAssetPrefill | null>(null);
  const [assetRefusal, setAssetRefusal] = useState<string | null>(null);

  // Save and Submit lands here with `act=submit`, so the submission runs against the saved Bill rather than a form that has not written.
  const asked = params.get('act');
  const [askDone, setAskDone] = useState(false);

  if (editing) return <BillForm billId={creating ? null : billId} apiBaseUrl={apiBaseUrl} />;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  /** `ok` is the notice a success shows; a function reads it off the server's answer, and a refusal always shows the server's own words. */
  const act = async (path: string, body: unknown, ok: string | ((answer: unknown) => string)): Promise<void> => {
    setBusy(true);
    const answer = await post<unknown>(`${BILLS_PATH}/${billId}/${path}`, body, apiBaseUrl);
    setBusy(false);
    // 🔒 A refused act leaves its window open with what was typed (decision 2870).
    if (answer.ok) setModal(null);
    // 🔒 Decision 3072: an approval decided here leaves the menu's Approvals balloon at once.
    if (answer.ok && (path === 'approve' || path === 'reject')) refreshMenuCounts();
    setNotice(answer.ok ? (typeof ok === 'string' ? ok : ok(answer.data)) : `!${answer.message}`);
    page.reload();
    print.reload();
  };

  /** 🔒 Decision 9700: Create Asset reads the line's prefill and opens the register's own New Asset window on it; a refusal reads as the page's notice. */
  const openAsset = async (lineId: string): Promise<void> => {
    setBusy(true);
    const answer = await get<BillAssetPrefill>(billLineAssetPath(billId, lineId), apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setNotice(`!${answer.message}`);
    setAssetRefusal(null);
    setAssetFrom(answer.data);
  };

  /** The save goes through the bill's route, which writes the node through the register's one create and links the line. */
  const saveAsset = async (from: BillAssetPrefill, draft: NodeDraft): Promise<void> => {
    setBusy(true);
    const answer = await post<{ id: string; name: string }>(billLineAssetPath(billId, from.lineId), { ...nodeBody(draft), parentNodeId: null, ownerCompanyId: draft.ownerCompanyId }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setAssetRefusal(answer.message);
    setAssetFrom(null);
    setNotice(t('bill.asset.created'));
    page.reload();
  };

  /**
   * 🔒 Save and Submit says which of three things happened (decision 0559): the bill posted, it waits for
   * approval, or it needs none and waits for somebody who posts bills. It once said "routed" whatever the gate
   * did, over a bill that stayed a Draft offering the same button, and read as doing nothing (found 2026-09-24).
   */
  const submitted = (answer: unknown): string => {
    const outcome = billSubmissionOutcome((answer ?? { approvalStatus: 'Pending', posted: false }) as BillSubmissionAnswer);
    if (outcome === 'posted') return t('bill.submitted.posted');
    if (outcome === 'clearedNotPosted') return t('bill.submitted.clearedNotPosted');
    return t('bill.submitted.pending');
  };

  if (asked === 'submit' && !askDone && !busy && page.state.kind === 'ready') {
    setAskDone(true);
    void act('submit', {}, submitted);
  }

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('bill.notFound')} onRetry={page.reload}>
      {(bill) => {
        const facts: ScreenFact[] = [
          { label: t('bill.fact.vendorInvoiceNumber'), value: bill.vendorInvoiceNumber },
          { label: t('bill.field.vendor'), value: bill.vendorName, route: `/companies/${bill.vendorCompanyId}` },
          { label: t('bill.fact.dueDate'), value: line(bill.dueDate, '·', bill.paymentTerms) },
          { label: t('bill.fact.purchaseOrder'), value: bill.sourcePurchaseOrderNumber, route: bill.sourcePurchaseOrderId ? `/purchase-orders/${bill.sourcePurchaseOrderId}` : null },
        ];
        // 🔒 The approval pair is drawn only for a viewer the queue's own answer allows on this bill (T2101; decisions 1521, 2863): a reader of Bills, or the person who raised it, sees neither.
        const actions = billActions({ status: bill.status, approvalStatus: bill.approvalStatus, mayApprove: bill.canApprove === true });
        const footer = (
          <>
            {/* 🔨 Edit is the action bar's primary and is drawn there alone (decision 0612). */}
            {actions.map((action) => (
              <Button
                key={action}
                size={Size.Medium}
                {...(action === 'approve' || action === 'submit' || action === 'pay' ? { color: Color.Navy } : {})}
                text={[ACTION_LABEL[action]()]}
                disabled={busy}
                actions={[
                  () => {
                    if (action === 'submit') void act('submit', {}, submitted);
                    else if (action === 'approve') void act('approve', {}, t('bill.approvedNotice'));
                    else if (action === 'resubmit') void act('resubmit', {}, submitted);
                    else setModal(action);
                  },
                ]}
              />
            ))}
            {/* 🔒 Decision 11020: a posted Bill recurs, for a viewer who may post; the window asks how often. */}
            {bill.canMakeRecurring === true && (bill.status === 'Received' || bill.status === 'PartiallyPaid' || bill.status === 'Paid') ? (
              <MakeRecurringButton sourceType="Bill" sourceId={bill.id} apiBaseUrl={apiBaseUrl} disabled={busy} onMade={(text) => setNotice(text)} />
            ) : null}
            {/* 🔨 The sheet's own control moved to the action bar above both views, so one document offers it once (decision 0612). */}
          </>
        );
        /** 🔒 The bill's lines as the one read-only table draws every kind's (decision 0612), with the variance marks in the row's own second line. */
        const lines: DocumentLinesDescriptor = {
          title: line(t('bill.lines.title'), '·', `${t('bill.variance.tolerance')} ${formatPercent(bill.varianceTolerancePercent)}`),
          rowAttribute: 'data-bill-line',
          blockAttribute: 'data-bill-lines',
          empty: t('bill.emptyLines'),
          rows: bill.lines.map((row) => ({
            id: row.id,
            label: row.sku ? line(row.sku, '·', row.description) : row.description,
            note: (
              <>
                {row.variance.message ? <span className={styles.variance} data-bill-variance>{line(t('bill.variance.heading'), '·', row.variance.message)}</span> : null}
                {/* 🔨 A skipped comparison says so, so an absent flag never reads as a pass (decision 0413). */}
                {row.variance.quantitySkip === 'notYetReceived' ? <span className={styles.skipped} data-bill-variance-skip="notYetReceived">{t('bill.variance.notYetReceived')}</span> : null}
                {row.variance.quantitySkip === 'noOrderLine' ? <span className={styles.skipped} data-bill-variance-skip="noOrderLine">{t('bill.variance.noOrderLine')}</span> : null}
                {/* 🔒 Decision 9700: a line that made an asset reads its name as a link, and Create Asset is offered on it no more. */}
                {row.asset ? (
                  <span className={styles.assetAction} data-bill-line-asset={row.asset.id}>
                    {line(t('bill.asset.linked'), '·')} <Link to={assetRoute(row.asset.id)}>{row.asset.name}</Link>
                  </span>
                ) : row.offersCreateAsset ? (
                  <span className={styles.assetAction} data-bill-create-asset={row.id}>
                    <Button size={Size.Small} text={[t('bill.action.createAsset')]} disabled={busy} actions={[() => void openAsset(row.id)]} />
                  </span>
                ) : null}
              </>
            ),
            cells: [line(String(row.quantity), row.unitName)],
            amount: <Money cents={row.extendedCents} currencyCode="USD" />,
            attributes: { 'data-bill-line-flagged': row.variance.flagged ? 'true' : 'false' },
          })),
          totals: [{
            label: line(t('bill.lines.total')),
            value: <><Money cents={bill.totalCents} currencyCode="USD" /> {line('·', t('bill.lines.paid'))} <Money cents={bill.amountPaidCents} currencyCode="USD" /></>,
            attributes: { 'data-bill-total': String(bill.totalCents) },
          }],
        };
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(bill);
        const notesPanel = <DocumentNotes recordType="Bill" recordId={bill.id} recordLabel={bill.billNumber} apiBaseUrl={apiBaseUrl} onError={(message) => setNotice(`!${message}`)} carried={carried} />;
        // One Account Journal tile, placed in the Details view and handed to the PDF view's rail as its Journal tab (T1070; decision 1662).
        const journalPanel = <AccountJournalTile sourceDocumentType="Bill" sourceDocumentId={bill.id} apiBaseUrl={apiBaseUrl} />;
        // One Files block, placed in the Details view and handed to the PDF view's rail as its Files tab (T1070; decision 1662).
        // 🔒 Drawn on every bill, files or none, because the mail tagged to the bill lists beneath it (decision 1663). The upload is
        // offered while the viewer may write the bill, as on every other document page; the panel decides (T2212).
        // 🔒 Decision 11480: each file offers Release to the Bill's vendor, through the one Release window.
        const filesPanel = (
          <section className={styles.block} data-bill-files={bill.files?.length ?? 0}>
            <h2 className={styles.heading}>{t('documentFiles.title')}</h2>
            <DocumentFilesPanel recordType="Bill" recordId={bill.id} files={bill.files ?? []} apiBaseUrl={apiBaseUrl} onChanged={page.reload} writeRefusal={bill.writeRefusal ?? null} releaseTo={vendorReleaseCompanies(bill.vendorCompanyId, bill.vendorName)} />
          </section>
        );
        // 🔒 One History panel, placed in the Details view and handed to the PDF view's rail as its History tab: the Bill's audit rows, an order
        // edit and a short receipt under its approval among them (decision 10310; T1070, decision 1662).
        const historyPanel = <DocumentHistoryPanel recordType="Bill" recordId={bill.id} rows={bill.history ?? []} timeZone={zone} />;
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            journal={journalPanel}
            history={historyPanel}
            documentType="Bill"
            icon="accounting"
            title={bill.billNumber}
            subtitle={pageKindLineText({ page: 'document', documentType: 'Bill' })}
            information={facts}
            status={<BannerPills pills={billPills(bill)} attribute="data-bill-pills" hint={bill.approval?.reason ?? null} />}
            footer={footer}
            footerAttributes={{ 'data-bill-actions': actions.join(' ') }}
            pageAttributes={{ 'data-bill': bill.id, 'data-bill-approval': bill.approvalStatus, 'data-bill-flagged': bill.varianceFlagged ? 'true' : 'false' }}
            print={print.state.kind === 'ready' ? print.state.data : null}
            layout={bill.printLayout ?? null}
            sheet={bill.printSheet ?? null}
            writeRefusal={bill.writeRefusal ?? null}
            ribbon={bill.status}
            pdfPath={documentPdfPath(BILLS_PATH, bill.id)}
            listRoute={LIST_PAGES.Bill.route}
            {...(bill.status === 'Draft' ? { onEdit: () => navigate(`/bills/${bill.id}?mode=edit`) } : {})}
            apiBaseUrl={apiBaseUrl}
            lines={lines}
            /* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */
            details={
              <>
                {/* 🔒 What this bill posted and where each amount was placed (decision 1075). */}
                {journalPanel}
                {/* 🔒 T346 (decision 1515): the files on the bill, the vendor's own PDF the accounting mailbox attached among them; the upload stays where it was. */}
                {filesPanel}
                {notesPanel}
                {historyPanel}
              </>
            }
            whatsNext={
              /* 🔒 The footer's own moves, named as the next step (decision 1732). */
              <WhatsNextBanner documentType="Bill" status={bill.status} disabled={busy} actions={actions.includes('pay') ? { pay: () => setModal('pay') } : {}} />
            }
            notices={
              <>
                {notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
                {/* 🔒 The approval block says why the bill rests where it does (T131; decision 0713). */}
                <ApprovalReason view={bill.approval} />
              {/*
                * 🔨 The vendor's credit is shown on the Bill as well as on the form, because a Bill built from a
                * purchase order never passed through the form. 🔒 Piece 27: the banner's action applies a credit
                * here rather than sending somebody to the vendor's page to find one.
                * 🔒 T740: it offers Apply credit only on a status the route accepts. A Draft says the credit applies
                * once the bill is submitted and posted, and every other status offers View credits, the same window
                * read-only, so the banner never invites an act the server refuses.
                */}
              {bill.availableCredit.length > 0 ? (
                <div data-bill-credit={bill.availableCredit.length} data-bill-credit-takes={billTakesCredit(bill.status) ? 'true' : 'false'}>
                  <AlertBanner
                    tone="warning"
                    message={<>{line(t('bill.credit.heading'), '—')} {bill.availableCredit.map((row, index) => <Fragment key={row.id}>{index > 0 ? ' · ' : null}{line(row.kind === 'VendorCredit' ? t('bill.credit.vendorCredit') : t('bill.credit.prepayment'), row.reference, '·')} <Money cents={row.amountCents} currencyCode="USD" /></Fragment>)}{line('.')} {billTakesCredit(bill.status) ? t('bill.credit.apply') : bill.status === 'Draft' ? t('bill.credit.applyOnceSubmitted') : t('bill.credit.nextBill')}</>}
                    action={{ label: billTakesCredit(bill.status) ? t('bill.credit.applyAction') : t('bill.credit.viewAction'), onAction: () => setApplyCreditOpen(true) }}
                  />
                </div>
              ) : null}

              {/* 🔨 A Bill on an order nothing has been received against asks a person to confirm the delivery; the order's own Receive posts because they said so. */}
              {bill.deliveryPrompt ? (
                <div data-bill-delivery={bill.deliveryPrompt.purchaseOrderNumber}>
                  <AlertBanner tone="warning" message={`${t('bill.delivery.heading')} — ${bill.deliveryPrompt.purchaseOrderNumber}. ${t('bill.delivery.body')}`} action={{ label: t('po.action.receive'), onAction: () => navigate(`/purchase-orders/${bill.deliveryPrompt!.purchaseOrderId}`) }} />
                </div>
              ) : null}

              </>
            }
          >
              {/* 🔨 Unmounted when closed, so reopening it on another bill does not show the first one's draft. */}
              {applyCreditOpen ? (
                <ApplyCreditModal
                  key={`applycredit-${bill.id}`}
                  source={{ billId: bill.id }}
                  apiBaseUrl={apiBaseUrl}
                  onCancel={() => setApplyCreditOpen(false)}
                  onDone={(message) => {
                    setApplyCreditOpen(false);
                    setNotice(message);
                    page.reload();
                  }}
                />
              ) : null}

              {/* 🔨 Decision 0429: paying one bill is a small act, so it is a modal on the Bill rather than a
                  page that starts by asking for the vendor of the bill already on the screen. It is keyed to the
                  bill it pays, so reopening it on another one never shows the first one's draft. */}
              {modal === 'pay' ? (
                <Suspense fallback={null}>
                <PayBillModal
                  key={`pay-${bill.id}`}
                  billId={bill.id}
                  vendorCompanyId={bill.vendorCompanyId}
                  billNumber={bill.billNumber}
                  apiBaseUrl={apiBaseUrl}
                  onCancel={() => setModal(null)}
                  onPaid={(said) => {
                    setModal(null);
                    // 🔨 Pay and Print Now says so when the check was left unnumbered; a refused sheet reads as the error it is (decision 1131).
                    setNotice(said ? `${said.tone === 'error' ? '!' : ''}${said.text}` : t('bill.paidNotice'));
                    page.reload();
                    print.reload();
                  }}
                />
                </Suspense>
              ) : null}

              {/* 🔒 Decision 9700: the register's own New Asset window, filled from the line and keyed to it, so another line never opens on this one's draft. */}
              {assetFrom ? (
                <Suspense fallback={null}>
                <NodeFormModal
                  key={`bill-asset-${assetFrom.lineId}`}
                  open
                  mode="new"
                  initial={{ ...emptyNodeDraft(null), name: assetFrom.name, acquiredOn: assetFrom.acquiredOn, acquisitionCostCents: assetFrom.acquisitionCostCents, ownerCompanyId: assetFrom.ownerCompanyId, ownerLabel: assetFrom.ownerName }}
                  source={line(t('bill.asset.from'), assetFrom.billNumber, '·', t('bill.asset.line'), assetFrom.lineNumber)}
                  refusal={assetRefusal}
                  apiBaseUrl={apiBaseUrl}
                  onCancel={() => setAssetFrom(null)}
                  onSave={(draft) => void saveAsset(assetFrom, draft)}
                />
                </Suspense>
              ) : null}

              {/* 🔒 One window per act, each holding its own reason, so a reason typed for Reject never opens in Void (decision 2870). */}
              <ReasonWindow
                open={modal === 'reject'}
                title={t('bill.reject.title')}
                label={t('bill.reject.reason')}
                actLabel={t('bill.action.reject')}
                busy={busy}
                onCancel={() => setModal(null)}
                refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                dataModal="reject-bill"
                onAct={(reason) => act('reject', { reason }, t('bill.rejectedNotice'))}
              />
              <ReasonWindow
                open={modal === 'void'}
                title={t('bill.void.title')}
                label={t('bill.void.reason')}
                actLabel={t('bill.action.void')}
                hint={t('bill.void.hint')}
                busy={busy}
                onCancel={() => setModal(null)}
                refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                dataModal="void-bill"
                onAct={(reason) => act('void', { reason }, t('bill.voidedNotice'))}
              />
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}

/** The route element: the bill named in the path, inside the list-and-detail frame (decision 0408). */
export function BillRoute({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { id } = useParams();
  return <BillPage billId={id ?? NEW_DOCUMENT_ID} apiBaseUrl={apiBaseUrl} />;
}
